import { usePermissionStore } from '../stores/permission'

type RouteLike = {
  requiredPermissions?: string[]
  children?: RouteLike[]
}

export function usePermissions() {
  const permissionStore = usePermissionStore()
  if (permissionStore.userPermissions.length === 0) {
    permissionStore.initialize()
  }

  const hasPermission = (permission: string) => {
    return permissionStore.userPermissions.includes(permission)
  }

  const hasAnyPermission = (permissions: string[]) => {
    if (permissions.length === 0) {
      return true
    }
    return permissions.some((permission) => hasPermission(permission))
  }

  const hasAllPermissions = (permissions: string[]) => {
    if (permissions.length === 0) {
      return true
    }
    return permissions.every((permission) => hasPermission(permission))
  }

  const canAccessRoute = (route: RouteLike) => {
    const required = route.requiredPermissions ?? []
    return hasAnyPermission(required)
  }

  const filterNavigationRoutes = (routes: RouteLike[]): RouteLike[] => {
    return routes
      .map((route) => {
        const children = route.children ? filterNavigationRoutes(route.children) : undefined
        const hasVisibleChildren = Boolean(children && children.length > 0)
        const visible = canAccessRoute(route) || hasVisibleChildren

        if (!visible) {
          return null
        }

        return {
          ...route,
          ...(children ? { children } : {}),
        }
      })
      .filter((route): route is RouteLike => route !== null)
  }

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessRoute,
    filterNavigationRoutes,
  }
}
